import { ref, string, addMethod } from 'yup';
import { locationData } from './location-data';

const EMAIL_TAKEN = 'That email is already registered.';

addMethod(string, 'hasLetter', function (message) {
    return this.test('has-letter', message, function (value) {
        if (!value) {
            return true;
        }

        return /[A-Za-z]/.test(value);
    });
});

const validationRules = {
    address1: string().required('The Office Address field is required'),
    address2: string(),
    city: string().hasLetter('The City field must have at least 1 letter').required('The City field is required'),
    confirmPassword: string().test('passwords-match', 'The Password Confirmation does not match', function (value) {
        return !this.parent.password || value === this.parent.password;
    }),
    country: string().required('The Country field is required'),
    currentPassword: string().required('The Current Password field is required to make changes'),
    department: string()
        .max(50, 'The Department field must be 50 characters or less')
        .hasLetter('The Department field must have at least 1 letter'),
    email: string()
        .email('The Corporate Email address is not valid')
        .required('The Corporate Email field is required')
        .test('is-taken', EMAIL_TAKEN, async (value) => {
            if (!value) {
                return true;
            }

            const response = await fetch(`/actions/auth/api/check-email?${new URLSearchParams({ email: value })}`);

            return (await response.text()) !== '1';
        }),
    firstName: string()
        .hasLetter('The First Name field must have at least 1 letter')
        .max(30, 'The First Name field must be 30 characters or less')
        .required('The First Name field is required'),
    lastName: string()
        .hasLetter('The Last Name field must have at least 1 letter')
        .max(50, 'The Last Name field must be 50 characters or less')
        .required('The Last Name field is required'),
    linkedin: string().matches(/^[A-Za-z0-9/_-]+$/, {
        message: 'The LinkedIn field format is invalid',
        excludeEmptyString: true,
    }),
    memberCompany: string()
        .hasLetter('The Company field must have at least 1 letter')
        .max(50, 'The Company field must be 50 characters or less')
        .required('The Company field is required'),
    memberTitle: string()
        .hasLetter('The Job Title field must have at least 1 letter')
        .max(50, 'The Job Title field must be 50 characters or less')
        .required('The Job Title field is required'),
    nickname: string()
        .max(30, 'The Nickname field may not be greater than 30 characters')
        .hasLetter('The Nickname field must have at least 1 letter')
        .nullable(),
    password: string()
        .min(8, 'The Password field must be at least 8 characters')
        .max(64, 'The Password field must be less than 64 characters')
        .required('The Password field is required'),
    phone: string()
        .matches(/^[\d() .+-]+$/, 'The Phone Number field format is invalid')
        .max(20, 'The Phone Number field must be 20 characters or less')
        .required('The Phone Number field is required'),
    state: string().test('country-state', 'The State field is required for the selected country', function (value) {
        return !Object.hasOwn(locationData, this.parent['fields[memberCountry]']) || value;
    }),
    timezone: string().required('The Time Zone field is required'),
    twitter: string()
        .test('empty-or-min', 'The Twitter field must be at least 4 characters', (value) => {
            if (value.length === 0) {
                return true;
            }

            return value.length >= 4;
        })
        .max(15, 'The Twitter field may not be greater than 15 characters')
        .matches(/^[A-z_\d]+$/, { message: 'The Twitter field format is invalid', excludeEmptyString: true }),
    zip: string().required('The Postal Code field is required').max(20),
};

const extractErrors = (errs) => {
    const errors = {};

    errs.inner.forEach((err) => {
        errors[err.path] = err.message;
    });

    return errors;
};

export { validationRules, EMAIL_TAKEN, extractErrors };
